import axios from './axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_INSURANCE', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export type GetAllCarriersResponse = ApiResponse<any>
export type SearchCarriersByNameResponse = ApiResponse<any>

const getCarrierReferenceValues = (): any => {
  return {
    user_id: JSON.parse(localStorage.getItem('user') || '{}')?.user_id || '',
    clinic_id: localStorage.getItem('clinic_id') || '',
    db_name: localStorage.getItem('db_name') || '',
  }
}

// API Functions
export const GetAllCarriers = async (): Promise<GetAllCarriersResponse> => {
  try {
    const response = await axios.get<GetAllCarriersResponse>(
      `${BUSINESS_LAYER_API}/api/v1/carrier/all`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetAllCarriers:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const searchCarriersByName = async (
  name: string,
): Promise<SearchCarriersByNameResponse> => {
  try {
    const { data, status } = await axios.get<SearchCarriersByNameResponse>(
      `${BUSINESS_LAYER_API}/api/v1/carrier/search?name=${name}`,
      {
        headers,
      },
    )

    // Check if the response status is 200, throw an error otherwise
    if (status !== 200) {
      throw new Error('Failed to fetch carriers')
    }

    return data
  } catch (error: any) {
    logger.error('Error fetching carriers:', error)
    throw toApiError(error, 'Error fetching carriers')
  }
}

export const getAllCarrierSettings = async (): Promise<any> => {
  try {
    const response = await axios.get<any>(
      `${BUSINESS_LAYER_API}/api/v1/carriersettings`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetAllCarrierSettings:', e)
    throw toApiError(e, 'Error fetching carrier settings')
  }
}

export const addCarrierSettings = async (data: any): Promise<any> => {
  try {
    const { user_id, clinic_id } = getCarrierReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/carriersettings`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error adding carrier settings:', error)
    throw toApiError(error, 'Error adding carrier settings')
  }
}

export const updateCarrierSettings = async (
  carrier_setting_id: string,
  data: any,
): Promise<any> => {
  try {
    const { user_id, clinic_id } = getCarrierReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/carriersettings/${carrier_setting_id}`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error updating carrier settings:', error)
    throw toApiError(error, 'Error updating carrier settings')
  }
}

export const deleteCarrierSettings = async (
  carrier_setting_id: string,
): Promise<any> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/carriersettings/${carrier_setting_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error deleting carrier settings:', error)
    throw toApiError(error, 'Error deleting carrier settings')
  }
}

export const checkCarrierIfExists = async (
  carrierId: string,
  excludeCarrierSettingId?: string,
): Promise<any> => {
  try {
    const { clinic_id } = getCarrierReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      ...(excludeCarrierSettingId && {
        exclude_carrier_setting_id: excludeCarrierSettingId,
      }),
    }

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/carriersettings/carrier-exists/${carrierId}`,
      payload,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkCarrierIfExists:', e)
    throw toApiError(e, 'Error checking carrier')
  }
}

export const getAllFeeSchedule = async (): Promise<any> => {
  try {
    const response = await axios.get<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in GetAllCarrierSettings:', e)
    throw toApiError(e, 'Error fetching carrier settings')
  }
}

export const addFeeSchedule = async (data: any): Promise<any> => {
  try {
    const { user_id, clinic_id } = getCarrierReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error adding fee schedule:', error)
    throw toApiError(error, 'Error adding fee schedule')
  }
}

export const updateFeeSchedule = async (
  fee_schedule_id: string,
  data: any,
): Promise<any> => {
  try {
    const { user_id, clinic_id } = getCarrierReferenceValues()
    data.user_id = user_id
    data.clinic_id = clinic_id
    const response = await axios.put<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules/${fee_schedule_id}`,
      data,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error updating fee schedule:', error)
    throw toApiError(error, 'Error updating fee schedule')
  }
}

export const deleteFeeSchedule = async (
  fee_schedule_id: string,
): Promise<any> => {
  try {
    const response = await axios.delete<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules/${fee_schedule_id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (error: any) {
    logger.error('Error deleting fee schedule:', error)
    throw toApiError(error, 'Error deleting fee schedule')
  }
}

export const checkFeeScheduleIfExists = async (
  feeScheduleName: string,
  excludeFeeScheduleId?: string,
): Promise<any> => {
  try {
    const { clinic_id } = getCarrierReferenceValues()
    const payload = {
      clinic_id: clinic_id,
      ...(excludeFeeScheduleId && {
        exclude_fee_schedule_id: excludeFeeScheduleId,
      }),
    }

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules/fee-schedule-exists/${feeScheduleName}`,
      payload,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkFeeScheduleIfExists:', e)
    throw toApiError(e, 'Error checking fee schedule')
  }
}

export const checkContractIfExists = async (data: any): Promise<any> => {
  try {
    const { clinic_id } = getCarrierReferenceValues()
    data.clinic_id = clinic_id

    const response = await axios.post<any>(
      `${BUSINESS_LAYER_API}/api/v1/feeschedules/contract-exists`,
      data,
      {
        headers,
      },
    )
    return response.data
  } catch (e: unknown) {
    logger.error('Error in checkContractIfExists:', e)
    throw toApiError(e, 'Error checking contract')
  }
}
